import { compact, groupBy } from 'lodash';

import { MODE_WATER, UNIT_CO2_UNKNOWN } from '@/lib/constants';
import {
  Co2EmissionTotal,
  EmissionsCalculationModel,
  EmissionsCalculationSegmentModel,
  EmissionsChartData,
} from '@/lib/models/emissions/types';
import { sumTotalCo2Emissions } from '@/lib/models/emissions/utils/count-emissions';
import { mergeEmissionStats } from '@/lib/models/emissions/utils/merge-emission-stats';
import { EmissionStats, RouteScheduleInterface } from '@/types';
import { convertSegmentModeVehicleType } from '@/utils/format/mode';
import { getTransportColor, getTransportTextColor } from '@/utils/helpers/get-transport-color';
import { roundFloatToFixed, roundNumberToFixed } from '@/utils/helpers/maths';

/**
 * Counts average emissions for all schedules
 * @NOTE: For old Schedules Model / API
 */
export const getAverageScheduleRouteEmissions = (schedules: RouteScheduleInterface[]): EmissionStats | undefined => {
  const emissions = compact(
    schedules.map((schedule) => {
      if (schedule.emissions) {
        return {
          min: schedule.emissions.wtw,
          max: schedule.emissions.wtw,
          average: schedule.emissions.wtw,
          unit: schedule.emissions.unit,
          mode: schedule.mode,
        } as EmissionStats;
      }
      return undefined;
    }),
  );

  return mergeEmissionStats(emissions);
};

export const transformEmissionsModelToModeBreakdown = (
  emissions?: EmissionsCalculationModel[],
): EmissionsChartData[] => {
  if (!emissions) return [];

  const groupedEmissions = groupBy(emissions, 'type');

  const byMode = Object.keys(groupedEmissions).reduce((accum, key) => {
    const mode = groupedEmissions[key];
    const totals = getEmissionCalculationTotals(mode);
    return [...accum, totals];
  }, [] as Co2EmissionTotal[]);

  const totals = getEmissionCalculationTotals(emissions);

  return byMode.map((mode) => {
    const color = getTransportColor(mode.type);
    const textColor = getTransportTextColor(mode.type);
    const vehicle = convertSegmentModeVehicleType(mode.type);

    return {
      name: vehicle,
      value: mode.co2e.wtw,
      color,
      textColor,
      percent: Math.round((mode.co2e.wtw / totals.co2e.wtw) * 100),
      data: mode,
    };
  });
};

export const getEmissionCalculationTotals = (emissions: EmissionsCalculationModel[]): Co2EmissionTotal => {
  const totals = emissions.reduce(
    (accum, row) => {
      const { co2e, distance, segments } = row;
      const segmentTotals = getSummedEmissionSegments(segments);
      const totalCo2 = sumTotalCo2Emissions([co2e, accum.co2e]);
      const totalDistance = accum.distance + distance;
      const totalActivity = accum.activity + segmentTotals.activity;
      const totalIntensity = accum.intensity + segmentTotals.intensity;

      return {
        co2e: totalCo2,
        distance: roundNumberToFixed(totalDistance),
        type: row.type,
        activity: roundNumberToFixed(totalActivity),
        intensity: totalIntensity,
      };
    },
    {
      co2e: {
        wtw: 0,
        wtt: 0,
        ttw: 0,
        unit: UNIT_CO2_UNKNOWN,
      },
      distance: 0,
      type: MODE_WATER,
      activity: 0,
      intensity: 0,
    } as Co2EmissionTotal,
  );

  return {
    ...totals,
    intensity: emissions.length ? roundFloatToFixed(totals.intensity / emissions.length) : 0,
  };
};

export const getSummedEmissionSegments = (segments: EmissionsCalculationSegmentModel[]) => {
  return {
    activity: segments.reduce((sum, item) => sum + item.activity, 0),
    distance: segments.reduce((sum, item) => sum + item.distance, 0),
    intensity: segments.reduce((sum, item) => sum + item.intensity, 0) / segments.length,
    co2e: {
      wtt: segments.reduce((sum, item) => sum + (item.co2e.wtt || 0), 0),
      ttw: segments.reduce((sum, item) => sum + (item.co2e.ttw || 0), 0),
      wtw: segments.reduce((sum, item) => sum + item.co2e.wtw, 0),
    },
  };
};
